import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { 
  LayoutDashboard, Users, FileText, FolderOpen, GraduationCap, 
  BookOpen, Languages, BookMarked, Settings, BarChart3, Shield, 
  ChevronLeft, LogOut, Menu, X, DollarSign, Calendar, MessageSquare,
  Award, Zap, Target, Link2, Globe, TrendingUp, Activity, Plug, HeartPulse,
  Newspaper, Send, Crosshair, Stethoscope, Brain, Quote, UserCircle, Trophy,
  Upload, Contact, Layers
} from 'lucide-react';
import { useState } from 'react';

const LOCALE_FLAGS = { en: 'EN', el: 'EL', fr: 'FR', es: 'ES', de: 'DE', pt: 'PT', tr: 'TR', it: 'IT', ru: 'RU', sq: 'SQ', bg: 'BG', zh: 'ZH', hi: 'HI', ar: 'AR' };

const ADMIN_NAV = [
  { label: 'Dashboard', labelKey: 'admin.dashboard', path: '/admin/dashboard', icon: LayoutDashboard },

  { type: 'section', label: 'Core', labelKey: 'admin.section_core' },
  { label: 'Programs', labelKey: 'admin.programs', path: '/admin/programs', icon: BookOpen },
  { label: 'Students', labelKey: 'admin.students', path: '/admin/students', icon: GraduationCap },
  { label: 'Admissions', labelKey: 'admin.applications', path: '/admin/applications', icon: FileText },
  { label: 'Lead Pipeline', labelKey: 'admin.lead_pipeline', path: '/admin/lead-pipeline', icon: Target },
  { label: 'Appointments', labelKey: 'admin.appointments', path: '/admin/appointments', icon: Calendar },
  { label: 'Finance', labelKey: 'admin.finance', path: '/admin/finance', icon: DollarSign },
  { label: 'Alumni', labelKey: 'admin.alumni', path: '/admin/alumni', icon: Award },

  { type: 'section', label: 'Content', labelKey: 'admin.section_content' },
  { label: 'Magazine', labelKey: 'admin.magazine', path: '/admin/magazine', icon: Newspaper },
  { label: 'Testimonials', labelKey: 'admin.testimonials', path: '/admin/testimonials', icon: Quote },
  { label: 'Site Content', labelKey: 'admin.site_content', path: '/admin/site-content', icon: FolderOpen },
  { label: 'Educators', labelKey: 'admin.educators', path: '/admin/educators', icon: UserCircle },
  { label: 'Authority', labelKey: 'admin.authority', path: '/admin/authority', icon: Trophy },
  { label: 'Glossary', labelKey: 'admin.glossary', path: '/admin/glossary', icon: BookMarked },

  { type: 'section', label: 'Marketing', labelKey: 'admin.section_marketing' },
  { label: 'Campaigns', labelKey: 'admin.campaigns', path: '/admin/campaigns', icon: Zap },
  { label: 'Marketing', labelKey: 'admin.marketing', path: '/admin/marketing', icon: Target },
  { label: 'Referrals', labelKey: 'admin.referrals', path: '/admin/referrals', icon: Link2 },
  { label: 'SEO', labelKey: 'admin.seo', path: '/admin/seo', icon: Globe },
  { label: 'SEO Health', labelKey: 'admin.seo_health', path: '/admin/seo-health', icon: Activity },
  { label: 'SEO Intelligence', labelKey: 'admin.seo_intelligence', path: '/admin/seo-intelligence', icon: Brain },
  { label: 'Tracking', labelKey: 'admin.tracking', path: '/admin/tracking', icon: Crosshair },
  { label: 'Diagnostics', labelKey: 'admin.diagnostics', path: '/admin/tracking-diagnostics', icon: Stethoscope },
  { label: 'Distribution', labelKey: 'admin.distribution', path: '/admin/distribution', icon: Send },
  { label: 'Communications', labelKey: 'admin.communications', path: '/admin/communications', icon: MessageSquare },
  { label: 'BI Reports', labelKey: 'admin.bi_reports', path: '/admin/bi', icon: TrendingUp },

  { type: 'section', label: 'Operations', labelKey: 'admin.section_operations' },
  { label: 'Student Migration', labelKey: 'admin.student_migration', path: '/admin/student-migration', icon: Upload },
  { label: 'Contacts', labelKey: 'admin.contacts', path: '/admin/contacts', icon: Contact },
  { label: 'Segments', labelKey: 'admin.segments', path: '/admin/segments', icon: Layers },
  { label: 'Connections', labelKey: 'admin.connections', path: '/admin/connections', icon: Plug },
  { label: 'Translations', labelKey: 'admin.translations', path: '/admin/translations', icon: Languages },
  { label: 'System Health', labelKey: 'admin.system_health', path: '/admin/system-health', icon: HeartPulse },
  { label: 'Users', labelKey: 'admin.users', path: '/admin/users', icon: Shield },
  { label: 'Audit', labelKey: 'admin.audit_logs', path: '/admin/audit', icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale, locales } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="admin-layout">
      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full bg-white border-r border-border/50 z-40 transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
        data-testid="admin-sidebar"
      >
        <div className={`flex items-center h-16 border-b border-border/50 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`}>
          {!collapsed && (
            <Link to="/en" className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              MISEL<span className="font-light">GROUP</span>
            </Link>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid="sidebar-toggle">
            <ChevronLeft size={18} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {ADMIN_NAV.map((item, idx) => {
            if (item.type === 'section') {
              if (collapsed) return <hr key={idx} className="my-2 mx-4 border-border/30" />;
              const sectionLabel = item.labelKey ? t(item.labelKey) : '';
              const sLabel = (sectionLabel && sectionLabel !== item.labelKey) ? sectionLabel : item.label;
              return (
                <div key={idx} className="mt-5 mb-1.5 px-5">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">{sLabel}</span>
                </div>
              );
            }
            const Icon = item.icon;
            const active = location.pathname === item.path;
            const translated = item.labelKey ? t(item.labelKey) : '';
            const label = (translated && translated !== item.labelKey) ? translated : item.label;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-xl text-[13px] transition-all duration-200 ${
                  active ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? label : undefined}
                data-testid={`admin-nav-${item.path.split('/').pop()}`}
              >
                <Icon size={16} strokeWidth={1.5} />
                {!collapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </nav>
        <div className={`border-t border-border/50 p-4 ${collapsed ? 'text-center' : ''}`}>
          {/* Language Switcher */}
          <div className="relative mb-3" data-testid="admin-lang-switcher">
            <button onClick={() => setLangOpen(!langOpen)} className={`flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full px-2 py-1.5 rounded-lg hover:bg-muted ${collapsed ? 'justify-center' : ''}`}>
              <Globe size={14} strokeWidth={1.5} />
              {!collapsed && <span>{LOCALE_FLAGS[locale] || locale.toUpperCase()}</span>}
            </button>
            {langOpen && (
              <div className={`absolute ${collapsed ? 'left-full ml-2' : 'left-0 right-0'} bottom-full mb-1 bg-white border border-border/50 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto py-1`}>
                {(locales || Object.keys(LOCALE_FLAGS)).map(loc => (
                  <button key={loc} onClick={() => { setLocale(loc); setLangOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors ${loc === locale ? 'bg-foreground/5 font-semibold' : ''}`}>
                    {LOCALE_FLAGS[loc] || loc.toUpperCase()} — {loc}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!collapsed && (
            <div className="text-xs text-muted-foreground mb-2 truncate">
              {user?.first_name} {user?.last_name}
            </div>
          )}
          <button
            onClick={logout}
            className={`flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors ${collapsed ? 'justify-center w-full' : ''}`}
            data-testid="admin-logout"
          >
            <LogOut size={16} strokeWidth={1.5} />
            {!collapsed && t('admin.sign_out')}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-border/50 z-40 flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2" data-testid="admin-mobile-toggle">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <span className="text-sm font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>MISEL ADMIN</span>
        <button onClick={logout} className="p-2"><LogOut size={18} strokeWidth={1.5} /></button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/20" onClick={() => setMobileOpen(false)}>
          <div className="w-[260px] bg-white h-full p-4" onClick={e => e.stopPropagation()}>
            <div className="mb-6 text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>MISEL<span className="font-light">GROUP</span></div>
            {ADMIN_NAV.map((item, idx) => {
              if (item.type === 'section') {
                return (
                  <div key={idx} className="mt-4 mb-1.5 px-3">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">{item.label}</span>
                  </div>
                );
              }
              const Icon = item.icon;
              const active = location.pathname === item.path;
              const translated = item.labelKey ? t(item.labelKey) : '';
              const label = item.label || ((translated && translated !== item.labelKey) ? translated : '');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] mb-0.5 ${active ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  <Icon size={16} strokeWidth={1.5} /><span>{label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'} mt-14 lg:mt-0`}>
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
